import { z } from 'zod';
import { analysisUrlSchema } from './analysis.ts';
import { DEVICES } from './events.ts';

export const MAX_EVENTS_PER_BATCH = 5000;

const jsonRecord = z.record(z.string(), z.unknown());

export const rrwebEventSchema = z.looseObject({
  type: z.number().int().min(0).max(10),
  timestamp: z.number().finite().nonnegative(),
  data: z.unknown(),
});

export const ingestMetaSchema = z.object({
  url: z.string().max(4096),
  referrer: z.string().max(4096).optional(),
  userAgent: z.string().max(1024),
  language: z.string().max(64).optional(),
  viewport: z.object({ w: z.number().int().nonnegative(), h: z.number().int().nonnegative() }),
  userId: z.string().max(256).optional(),
  traits: jsonRecord.optional(),
});

export const ingestPayloadSchema = z.object({
  key: z.string().min(1).max(128),
  sessionId: z.uuid(),
  visitorId: z.uuid(),
  seq: z.number().int().nonnegative().max(1_000_000),
  meta: ingestMetaSchema.optional(),
  events: z.array(rrwebEventSchema).max(MAX_EVENTS_PER_BATCH),
});

export const pageviewPayloadSchema = z.object({
  url: z.string().max(4096),
  path: z.string().max(2048),
  title: z.string().max(512).optional(),
});

export const clickPayloadSchema = z.object({
  path: z.string().max(2048),
  pageX: z.number().finite(),
  pageY: z.number().finite(),
  docW: z.number().finite().nonnegative(),
  docH: z.number().finite().nonnegative(),
  viewportW: z.number().finite().nonnegative(),
  selector: z.string().max(1024),
  offsetXPct: z.number().finite(),
  offsetYPct: z.number().finite(),
});

export const trackPayloadSchema = z.object({
  name: z.string().min(1).max(256),
  props: jsonRecord.optional(),
});

export const identifyPayloadSchema = z.object({
  userId: z.string().min(1).max(256),
  traits: jsonRecord.optional(),
});

const originSchema = z
  .string()
  .trim()
  .refine((v) => v === '*' || /^https?:\/\/(\*\.)?[^/\s]+$/.test(v), {
    message: 'Use a full origin like https://example.com, https://*.example.com or *',
  });

const projectFields = {
  name: z.string().trim().min(1).max(120),
  allowedOrigins: z.array(originSchema).max(50).default([]),
  retentionDays: z.number().int().min(1).max(3650).default(30),
  /** The site the project tests (accepts `example.com`); its origin is allowed when no origin is given. */
  siteUrl: analysisUrlSchema.nullable().optional(),
};

export const createProjectSchema = z.object({
  ...projectFields,
  /** Adds the ready-made personas, so a new customer can start a test right away. */
  withPresetPersonas: z.boolean().default(false),
  /** Needed with a site: the person confirms they own it or may test it. */
  siteConsent: z.boolean().optional(),
});

export const updateProjectSchema = z.object({ ...projectFields, siteConsent: z.boolean() }).partial();

export const funnelStepSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('pageview'), pathPattern: z.string().trim().min(1).max(512) }),
  z.object({ type: z.literal('event'), name: z.string().trim().min(1).max(256) }),
]);

export const funnelQuerySchema = z.object({
  steps: z.array(funnelStepSchema).min(1).max(10),
  from: z.iso.datetime({ offset: true }).optional(),
  to: z.iso.datetime({ offset: true }).optional(),
});

export const saveFunnelSchema = z.object({
  name: z.string().trim().min(1).max(120),
  steps: z.array(funnelStepSchema).min(1).max(10),
});

export const deviceSchema = z.enum(DEVICES as unknown as ['desktop', 'tablet', 'mobile']);

export type FunnelStep = z.infer<typeof funnelStepSchema>;
export type FunnelQuery = z.infer<typeof funnelQuerySchema>;
export type CreateProjectInput = z.input<typeof createProjectSchema>;
export type UpdateProjectInput = z.input<typeof updateProjectSchema>;
